import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { formatViewCount } from '../../utils/formatViewCount';

export const ViewCountDisplay: React.FC<{
  count: number;
  className?: string;
  /** Compact pill for book cover overlay */
  variant?: 'default' | 'cover';
  /** `start`: eye then count (LTR hero). `end`: count then eye (RTL hero). */
  iconPosition?: 'start' | 'end';
  dataTourId?: string;
}> = ({ count, className = '', variant = 'default', iconPosition = 'start', dataTourId }) => {
  const { t } = useTranslation('learning');
  const label = formatViewCount(count);
  const ariaLabel = t('dashboardExtra.viewCountAria', { count: count.toLocaleString() });

  if (variant === 'cover') {
    return (
      <span
        className={`inline-flex max-w-full items-center gap-px rounded px-0.5 py-px text-[7px] font-semibold leading-none text-white bg-black/55 backdrop-blur-[2px] sm:gap-0.5 sm:text-[8px] ${className}`.trim()}
        title={ariaLabel}
        aria-label={ariaLabel}
      >
        <Eye className="h-2 w-2 shrink-0 opacity-90 sm:h-2.5 sm:w-2.5" aria-hidden />
        {label}
      </span>
    );
  }

  const eye = <Eye className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />;

  return (
    <span
      data-tour-id={dataTourId}
      className={`inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums leading-none text-gray-500 dark:text-gray-400 ${className}`.trim()}
      title={ariaLabel}
      aria-label={ariaLabel}
    >
      {iconPosition === 'start' ? (
        <>
          {eye}
          {label}
        </>
      ) : (
        <>
          {label}
          {eye}
        </>
      )}
    </span>
  );
};
